import React, { useEffect, useState } from 'react';
import { isTeamsHost } from '../utils/teamsHost';
import { hasTeamsSession } from '../utils/teamsSession';
import { useAuth } from "react-oidc-context";
import DevAuthHelper from '../utils/DevAuthHelper';
import { isDevModeLocalOverrideEnabled, persistDevOidcUser } from '../utils/devMode';
import { persistOidcUserJson } from '../utils/oidcStorage';
import { buildTeamsAuthStartUrl, clearTeamsAuthInProgress, markTeamsAuthInProgress } from '../utils/teamsAuthFlow';
import { getTeamsAuthenticationModule } from '../utils/teamsSdk';

type LoginPageProps = {
  teamsHostConfirmed?: boolean;
};

const LoginPage: React.FC<LoginPageProps> = ({ teamsHostConfirmed = false }) => {
  const { signinPopup } = useAuth();
  const [showTokenImport, setShowTokenImport] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const [authError, setAuthError] = useState<string>('');
  const [signingIn, setSigningIn] = useState(false);
  const [popupHint, setPopupHint] = useState(false);
  const [teamsAuthModule, setTeamsAuthModule] = useState<any>(null);

  // Track viewport size so the login card can shrink its chrome at low resolutions (~1280x800 and below).
  const [viewportSize, setViewportSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  useEffect(() => {
    const handleResize = () => setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isCompact = viewportSize.width <= 1280 || viewportSize.height <= 800;

  // Check for explicit dev mode override first
  const devModeOverride = isDevModeLocalOverrideEnabled();

  // Only an actual Teams host should use Teams authentication APIs. A copied content URL
  // with ?teams=true in a normal browser tab must not use Teams authenticate().
  // Teams iframe, or TeamsIntegration confirmed SDK context.
  const isInTeams = !devModeOverride && (teamsHostConfirmed || isTeamsHost() || hasTeamsSession());

  // Dev auth bypass is only allowed for explicit local/dev sessions.
  // Never infer dev mode from deploy-time flags on hosted review/prod URLs.
  const isDevelopment = devModeOverride || (!isInTeams && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('devtunnels.ms')
  ));

  console.log('LoginPage:', { isInTeams, isDevelopment, hostname: window.location.hostname });

  const hasStoredToken = DevAuthHelper.hasValidToken();

  // Pre-initialize the Teams SDK so the sign-in click can call authenticate()
  // synchronously. Awaiting SDK init inside the click handler consumes the
  // user gesture, and Teams (web) then silently refuses to open the popup.
  useEffect(() => {
    if (!isInTeams) return;
    let cancelled = false;

    getTeamsAuthenticationModule()
      .then((mod) => {
        if (!cancelled) {
          console.log('Teams authentication module ready');
          setTeamsAuthModule(mod);
        }
      })
      .catch((error) => {
        const msg = error instanceof Error ? error.message : String(error);
        // Expected when testing with ?teams=true in a plain browser — no Teams frame present.
        if (/no parent window|initialization failed/i.test(msg)) {
          console.warn('[LoginPage] Teams SDK not available (no Teams frame):', msg);
          return;
        }
        console.error('Teams SDK pre-initialization failed:', error);
        if (!cancelled) {
          setAuthError(`Teams SDK failed to initialize: ${msg}`);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isInTeams]);

  const signInWithTeams = () => {
    // Must use the pre-warmed module synchronously — any await before authenticate()
    // consumes the user gesture and Teams may refuse to open the popup.
    const teamsAuth = teamsAuthModule;
    if (!teamsAuth) {
      throw new Error('Teams SDK is still starting. Wait a moment and try again.');
    }

    const authStartUrl = buildTeamsAuthStartUrl(window.location.origin);
    markTeamsAuthInProgress();

    console.log('Opening Teams authentication popup:', authStartUrl);
    // No awaits between the click and this call — popup must open within the user gesture.
    const authPromise: Promise<string> = teamsAuth.authenticate({
      url: authStartUrl,
      width: 600,
      height: 535,
    });

    const hintTimer = window.setTimeout(() => setPopupHint(true), 6000);
    let timeoutId: number | undefined;
    const backstop = new Promise<never>((_, reject) => {
      timeoutId = window.setTimeout(
        () => reject(new Error('Teams did not return from the sign-in window. If no window opened, close and reopen the app tab, then try again.')),
        180000,
      );
    });

    return Promise.race([authPromise, backstop])
      .then((result) => {
        clearTeamsAuthInProgress();
        if (typeof result === 'string' && result.trim().startsWith('{')) {
          persistOidcUserJson(result);
        }
        window.location.reload();
      })
      .finally(() => {
        window.clearTimeout(hintTimer);
        if (timeoutId) window.clearTimeout(timeoutId);
        setPopupHint(false);
      });
  };

  const handleTokenImport = () => {
    if (tokenInput.trim()) {
      DevAuthHelper.storeSSOToken(tokenInput.trim());
      setTokenInput('');
      setShowTokenImport(false);
      // Refresh to pick up the new token
      window.location.reload();
    }
  };

  const handleClearToken = () => {
    DevAuthHelper.clearSSOToken();
    window.location.reload();
  };

  const handleAutoDetect = async () => {
    setTokenInput('Trying to detect SSO token...');

    // First try cookies from current domain
    const cookieToken = DevAuthHelper.getSSOTokenFromCookie();
    if (cookieToken) {
      setTokenInput(cookieToken);
      return;
    }

    // Try iframe approach (will likely fail due to CORS)
    const iframeToken = await DevAuthHelper.tryGetTokenViaIframe();
    if (iframeToken) {
      setTokenInput(iframeToken);
      return;
    }

    setTokenInput('Auto-detection failed due to CORS. Please copy manually or use browser extension.');
  };

  const handleDevSignIn = () => {
    // Same store as AuthProvider's WebStorageStateStore (localStorage), not sessionStorage.
    persistDevOidcUser();
    window.location.reload();
  };

  const handleSignIn = async () => {
    setAuthError('');
    setSigningIn(true);

    try {
      if (isDevelopment) {
        handleDevSignIn();
        return;
      }

      if (isInTeams) {
        await signInWithTeams();
        return; // reload happens in signInWithTeams on success
      }

      // Clear any legacy Teams marker so browser /callback is not hijacked.
      clearTeamsAuthInProgress();
      localStorage.removeItem('cw-ai-teams-auth-in-progress');

      await signinPopup();
      window.location.reload();
    } catch (error) {
      console.error('Sign-in failed:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setAuthError(
        isInTeams
          ? `Unable to complete ConnectWise SSO in Teams.${errorMessage ? ` ${errorMessage}` : ' Complete sign-in in the Teams popup window.'}`
          : 'Unable to start ConnectWise SSO. Please try again.'
      );
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center overflow-y-auto ${isCompact ? 'p-3' : 'p-4'}`}>
      <div className={`max-w-md w-full ${isCompact ? 'space-y-4' : 'space-y-8'}`}>
        <div className="text-center">
          {/* ConnectWise Colleague Assistant Logo */}
          <div className={`flex justify-center ${isCompact ? 'mb-3' : 'mb-8'}`}>
            <svg width={isCompact ? '96' : '180'} height={isCompact ? '96' : '180'} viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <defs>
                <linearGradient id="loginStarGrad" x1="0" y1="8" x2="16" y2="8" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#38C5F5" />
                  <stop offset="100%" stopColor="#B0E354" />
                </linearGradient>
                <style>{`
                  @keyframes loginPulse1 {
                    0%   { transform: scale(1); }
                    20%  { transform: scale(1); }
                    32%  { transform: scale(1.10); }
                    42%  { transform: scale(0.92); }
                    54%  { transform: scale(1.08); }
                    64%  { transform: scale(0.94); }
                    74%  { transform: scale(1.06); }
                    84%  { transform: scale(0.98); }
                    100% { transform: scale(1); }
                  }
                  .login-star1 {
                    animation: loginPulse1 20s ease-in-out infinite;
                    transform-box: fill-box;
                    transform-origin: center;
                  }
                  @keyframes loginPulse2 {
                    0%   { transform: scale(1); }
                    25%  { transform: scale(1); }
                    36%  { transform: scale(1.10); }
                    46%  { transform: scale(0.92); }
                    57%  { transform: scale(1.08); }
                    67%  { transform: scale(0.94); }
                    76%  { transform: scale(1.06); }
                    86%  { transform: scale(0.98); }
                    100% { transform: scale(1); }
                  }
                  .login-star2 {
                    animation: loginPulse2 18s ease-in-out infinite;
                    transform-box: fill-box;
                    transform-origin: center;
                  }
                `}</style>
              </defs>
              <path className="login-star1" d="M12.4997 9.94253C12.067 10.2382 11.4938 10.1864 11.1249 9.82229C9.71614 8.45662 7.6394 8.06575 5.83061 8.82587C5.35452 9.03108 4.80165 8.87091 4.50598 8.43816C4.21039 8.00552 4.26212 7.43231 4.62614 7.06337C5.99199 5.65465 6.38273 3.57777 5.62256 1.76884C5.4175 1.29281 5.57768 0.740008 6.01035 0.444398C6.44299 0.148806 7.01623 0.200517 7.38516 0.564588C8.794 1.93032 10.8709 2.32127 12.6798 1.56095C13.1558 1.35594 13.7085 1.51616 14.0041 1.94877C14.2997 2.38141 14.248 2.95456 13.884 3.32349C12.5181 4.73222 12.1273 6.80911 12.8875 8.61803C13.0926 9.09407 12.9324 9.64692 12.4997 9.94253Z" fill="url(#loginStarGrad)" />
              <path className="login-star2" d="M1.69207 11.073C1.40787 10.5905 2.00003 10.064 2.44591 10.4028C3.44203 11.1439 4.78952 11.223 5.86558 10.6037C6.34796 10.3196 6.87439 10.9118 6.53574 11.3576C5.79474 12.3537 5.71544 13.7012 6.33481 14.7772C6.619 15.2597 6.02684 15.7862 5.58097 15.4474C4.58484 14.7063 3.23736 14.6272 2.16129 15.2465C1.67892 15.5306 1.15248 14.9384 1.49113 14.4926C2.23213 13.4965 2.31143 12.149 1.69207 11.073Z" fill="url(#loginStarGrad)" />
            </svg>
          </div>

          <h2 className={`font-bold text-white ${isCompact ? 'text-xl mb-1' : 'text-3xl mb-2'}`}>
            ConnectWise Colleague Assistant
          </h2>
          <p className={`text-blue-200 ${isCompact ? 'text-sm mb-3' : 'text-lg mb-8'}`}>
            Your AI-powered business assistant
          </p>
        </div>

        <div className={`bg-white/10 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 ${isCompact ? 'p-4' : 'p-8'}`}>
          <div className={isCompact ? 'space-y-3' : 'space-y-6'}>
            <div className="text-center">
              <h3 className={`font-semibold text-white ${isCompact ? 'text-lg mb-1' : 'text-xl mb-2'}`}>
                Sign in to continue
              </h3>
              <p className="text-blue-200 text-sm">
                {isDevelopment
                  ? "Development mode - authentication bypassed"
                  : "Use your ConnectWise account to access the AI Assistant"}
              </p>
              {isDevelopment && (
                <div className="mt-2 px-3 py-1 bg-yellow-500/20 rounded-full border border-yellow-400/30">
                  <span className="text-yellow-200 text-xs font-medium">🚀 DEV MODE</span>
                </div>
              )}
            </div>

            <button
              onClick={handleSignIn}
              disabled={signingIn || (isInTeams && !isDevelopment && !teamsAuthModule)}
              className={`w-full flex items-center justify-center px-4 ${isCompact ? 'py-2' : 'py-3'} border border-transparent rounded-lg shadow-sm text-sm font-medium text-white ${isDevelopment
                ? 'bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 focus:ring-yellow-500'
                : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:ring-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`}
            >
              {signingIn ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {isDevelopment ? "Bypassing..." : "Signing in..."}
                </>
              ) : isInTeams && !isDevelopment && !teamsAuthModule ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Preparing Teams…
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013 3v1" />
                  </svg>
                  {isInTeams ? 'Sign in with ConnectWise SSO (Teams)' : 'Sign in with ConnectWise SSO'}
                </>
              )}
            </button>

            {/* Popup-not-visible hint while waiting on Teams auth window */}
            {signingIn && popupHint && (
              <div className="bg-amber-500/10 border border-amber-400/30 rounded-lg p-3">
                <p className="text-amber-200 text-sm">
                  A ConnectWise sign-in window should be open. If you don't see it, it may be behind
                  the Teams window, or Teams blocked it — close this tab in Teams, reopen the app,
                  and try again.
                </p>
              </div>
            )}

            {/* Error Display */}
            {authError && (
              <div className="bg-red-500/10 border border-red-400/30 rounded-lg p-3">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-red-200 text-sm">{authError}</span>
                </div>
              </div>
            )}

            {/* Teams-specific info */}
            {isInTeams && !isDevelopment && (
              <div className="bg-blue-500/10 border border-blue-400/30 rounded-lg p-3">
                <div className="flex items-start">
                  <svg className="w-4 h-4 text-blue-400 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-blue-200 text-sm">
                    <p className="font-medium mb-1">Microsoft Teams Integration</p>
                    <p>ConnectWise SSO opens in a Teams popup. Sign in there to return to the app.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Development Token Import Section */}
            {isDevelopment && (
              <div className={isCompact ? 'space-y-2' : 'space-y-4'}>
                <div className="flex items-center">
                  <div className="flex-1 border-t border-white/20"></div>
                  <span className="px-3 text-xs text-blue-300">OR</span>
                  <div className="flex-1 border-t border-white/20"></div>
                </div>

                {hasStoredToken ? (
                  <div className="bg-green-500/10 border border-green-400/30 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-green-200 text-sm">Real SSO Token Loaded</span>
                      </div>
                      <button
                        onClick={handleClearToken}
                        className="text-xs text-red-300 hover:text-red-200 underline"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {!showTokenImport ? (
                      <>
                        <button
                          onClick={() => setShowTokenImport(true)}
                          className="w-full flex items-center justify-center px-4 py-2 border border-blue-400/50 rounded-lg text-sm font-medium text-blue-200 hover:bg-blue-500/10 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Use Real SSO Token
                        </button>
                        <button
                          onClick={() => {
                            console.log('%c🔧 Browser Extension Instructions:', 'font-size: 16px; font-weight: bold; color: #10b981;');
                            console.log('1. Load the browser extension from: frontend/browser-extension/');
                            console.log('2. Go to chrome://extensions/ → Enable Developer Mode');
                            console.log('3. Click "Load unpacked" → Select the browser-extension folder');
                            console.log('4. Click the extension icon → "Get SSO Token" → "Send to Dev App"');
                            console.log('🚀 This will automatically extract and inject your SSO token!');
                          }}
                          className="w-full mt-2 px-4 py-2 bg-green-600/20 border border-green-500/50 rounded-lg text-sm font-medium text-green-200 hover:bg-green-500/20 focus:outline-none focus:ring-2 focus:ring-green-500/50 transition-all duration-200"
                        >
                          📋 Show Extension Instructions
                        </button>
                        <button
                          onClick={handleSignIn}
                          className="w-full flex items-center justify-center px-4 py-2 border border-blue-400/50 rounded-lg text-sm font-medium text-blue-200 hover:bg-blue-500/10 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Sign in with SSO
                        </button>
                      </>
                    ) : (
                      <div className="space-y-3">
                        <div className="bg-blue-500/10 border border-blue-400/30 rounded-lg p-3">
                          <div className="text-xs text-blue-200 mb-2">
                            <strong>Option A:</strong> Try auto-detection (may not work due to CORS)<br />
                            <strong>Option B:</strong> Manual copy from <a href="https://control.itsupport247.net/" target="_blank" rel="noopener noreferrer" className="underline text-blue-100">control.itsupport247.net</a><br />
                            <strong>Option C:</strong> Use browser extension (see console for instructions)
                          </div>
                          <button
                            onClick={handleAutoDetect}
                            className="w-full mb-3 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
                          >
                            🔍 Try Auto-Detect SSO Token
                          </button>
                          <textarea
                            value={tokenInput}
                            onChange={(e) => setTokenInput(e.target.value)}
                            placeholder="Paste your sso-token here..."
                            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white text-sm placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            rows={3}
                          />
                          <div className="flex space-x-2 mt-2">
                            <button
                              onClick={handleTokenImport}
                              disabled={!tokenInput.trim()}
                              className="flex-1 px-3 py-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:opacity-50 text-white text-xs rounded focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                            >
                              Import Token
                            </button>
                            <button
                              onClick={() => { setShowTokenImport(false); setTokenInput(''); }}
                              className="flex-1 px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white text-xs rounded focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="text-center">
              <p className="text-blue-200 text-xs">
                {isDevelopment
                  ? (hasStoredToken ? "🔑 Using real SSO token for authentic API calls" : "⚡ Development bypass - No real authentication required")
                  : "Secure authentication powered by ConnectWise"}
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LoginPage;
